import { useState, useEffect, useCallback } from 'react';
import { getDefaultEnabledDomains, getDefaultLayout } from '../config/domains';

const STORAGE_KEY = 'mabel_widget_layouts';

/**
 * useWidgetLayout - Manages widget layouts across all domains
 * 
 * Stores:
 * - enabledDomains: which domains appear in sidebar
 * - domainLayouts: custom widget arrangements per domain (null = use default)
 * - sidebarExpanded: current sidebar state
 * - sidebarPinned: keep sidebar expanded
 */
export function useWidgetLayout() {
  // Load initial state from localStorage
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Clean up any empty domainLayouts (they should use defaults)
        if (parsed.domainLayouts) {
          Object.keys(parsed.domainLayouts).forEach(domainId => {
            if (!parsed.domainLayouts[domainId]?.length) {
              delete parsed.domainLayouts[domainId];
            }
          });
        }

        // One-time migration: add Money domain for installs saved before it existed
        if (!parsed.migratedMoneyDomain) {
          if (Array.isArray(parsed.enabledDomains) && !parsed.enabledDomains.includes('money')) {
            parsed.enabledDomains = [...parsed.enabledDomains, 'money'];
          }
          if (parsed.domainLayouts?.hub && !parsed.domainLayouts.hub.includes('moneyCard')) {
            parsed.domainLayouts.hub = [...parsed.domainLayouts.hub, 'moneyCard'];
          }
          parsed.migratedMoneyDomain = true;
        }
        
        return parsed;
      }
    } catch (err) {
      console.warn('Failed to load widget layouts:', err);
    }
    
    // Default state
    return {
      enabledDomains: getDefaultEnabledDomains(),
      domainLayouts: {}, // empty = all use defaults
      sidebarExpanded: false,
      sidebarPinned: false,
    };
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      console.warn('Failed to save widget layouts:', err);
    }
  }, [state]);

  // ============================================
  // DOMAIN MANAGEMENT
  // ============================================

  /**
   * Get currently enabled domains (in order)
   */
  const enabledDomains = state.enabledDomains;

  /**
   * Enable a domain (add to sidebar)
   */
  const enableDomain = useCallback((domainId) => {
    setState(prev => {
      if (prev.enabledDomains.includes(domainId)) {
        return prev; // Already enabled
      }
      return {
        ...prev,
        enabledDomains: [...prev.enabledDomains, domainId],
      };
    });
  }, []);

  /**
   * Disable a domain (remove from sidebar)
   */
  const disableDomain = useCallback((domainId) => {
    setState(prev => ({
      ...prev,
      enabledDomains: prev.enabledDomains.filter(id => id !== domainId),
    }));
  }, []);

  /**
   * Reorder domains in sidebar
   */
  const reorderDomains = useCallback((newOrder) => {
    setState(prev => ({
      ...prev,
      enabledDomains: newOrder,
    }));
  }, []);

  // ============================================
  // WIDGET LAYOUT MANAGEMENT
  // ============================================

  /**
   * Get widget layout for a domain
   * Returns custom layout or default if not customized
   */
  const getLayout = useCallback((domainId) => {
    const customLayout = state.domainLayouts[domainId];
    if (customLayout && customLayout.length > 0) {
      return customLayout;
    }
    return getDefaultLayout(domainId);
  }, [state.domainLayouts]);

  /**
   * Set custom widget layout for a domain
   */
  const setLayout = useCallback((domainId, widgetIds) => {
    setState(prev => ({
      ...prev,
      domainLayouts: {
        ...prev.domainLayouts,
        [domainId]: widgetIds,
      },
    }));
  }, []);

  /**
   * Reset a domain's layout to default
   */
  const resetLayout = useCallback((domainId) => {
    setState(prev => {
      const newLayouts = { ...prev.domainLayouts };
      delete newLayouts[domainId];
      return {
        ...prev,
        domainLayouts: newLayouts,
      };
    });
  }, []);

  /**
   * Check if domain has custom layout
   */
  const hasCustomLayout = useCallback((domainId) => {
    return !!state.domainLayouts[domainId];
  }, [state.domainLayouts]);

  /**
   * Add a widget to a domain's layout
   */
  const addWidget = useCallback((domainId, widgetId) => {
    const currentLayout = getLayout(domainId);
    if (currentLayout.includes(widgetId)) {
      return; // Already in layout
    }
    setLayout(domainId, [...currentLayout, widgetId]);
  }, [getLayout, setLayout]);

  /**
   * Remove a widget from a domain's layout
   */
  const removeWidget = useCallback((domainId, widgetId) => {
    const currentLayout = getLayout(domainId);
    setLayout(domainId, currentLayout.filter(id => id !== widgetId));
  }, [getLayout, setLayout]);

  /**
   * Reorder widgets within a domain
   */
  const reorderWidgets = useCallback((domainId, newOrder) => {
    setLayout(domainId, newOrder);
  }, [setLayout]);

  /**
   * Move a widget up or down in the layout
   */
  const moveWidget = useCallback((domainId, widgetId, direction) => {
    const currentLayout = getLayout(domainId);
    const index = currentLayout.indexOf(widgetId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= currentLayout.length) return;

    const newLayout = [...currentLayout];
    [newLayout[index], newLayout[newIndex]] = [newLayout[newIndex], newLayout[index]];
    setLayout(domainId, newLayout);
  }, [getLayout, setLayout]);

  // ============================================
  // SIDEBAR STATE
  // ============================================

  const sidebarExpanded = state.sidebarExpanded;
  const sidebarPinned = state.sidebarPinned;

  const setSidebarExpanded = useCallback((expanded) => {
    setState(prev => ({
      ...prev,
      sidebarExpanded: expanded,
    }));
  }, []);

  const setSidebarPinned = useCallback((pinned) => {
    setState(prev => ({
      ...prev,
      sidebarPinned: pinned,
      sidebarExpanded: pinned, // If pinning, expand. If unpinning, keep current.
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState(prev => ({
      ...prev,
      sidebarExpanded: !prev.sidebarExpanded,
    }));
  }, []);

  // ============================================
  // RESET ALL
  // ============================================

  const resetAll = useCallback(() => {
    setState({
      enabledDomains: getDefaultEnabledDomains(),
      domainLayouts: {},
      sidebarExpanded: false,
      sidebarPinned: false,
    });
  }, []);

  return {
    // Domain management
    enabledDomains,
    enableDomain,
    disableDomain,
    reorderDomains,
    
    // Widget layout management
    getLayout,
    setLayout,
    resetLayout,
    hasCustomLayout,
    addWidget,
    removeWidget,
    reorderWidgets,
    moveWidget,
    
    // Sidebar state
    sidebarExpanded,
    sidebarPinned,
    setSidebarExpanded,
    setSidebarPinned,
    toggleSidebar,
    
    // Reset
    resetAll,
  };
}

export default useWidgetLayout;
